import {
    Modal,
    Paper,
    Box,
    Container,
    Typography,
    Divider,
    Grid,
    Stack,
    RadioGroup,
    Link,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as IconLock } from '../../assets/svg/lock-otp.svg';
import { ReactComponent as IconExit } from '../../assets/svg/exit.svg';
import { Button } from '../../components/button';
import { useGetRebookingInfo } from '../rebooking/hooks/use-get-rebooking-info';
import { useKeepReleaseModalStore } from '../rebooking/store';
import { useSetKeepReleaseRoomIntention } from '../rebooking/hooks/use-set-keep-release-room-intention';
import { RebookingDecision } from '../../utils/enum/RebookingDecision';

import { RebookingChoicesRadio } from './RebookingChoicesRadio';
import { RebookingModalHeader } from './rebooking-modal-header';
import { getRebookingInfoUrl } from './get-rebooking-info-url';

type RebookingChoicesModalProps = {
    developmentSlug: string;
};

export const RebookingChoicesModal: FC<RebookingChoicesModalProps> = ({ developmentSlug }) => {
    const { t } = useTranslation();
    const { data, isLoading, refetch } = useGetRebookingInfo();
    const { open, closeModal, openModal } = useKeepReleaseModalStore();
    const { mutate: setRebookingIntention, isLoading: isSettingRebookIntention } =
        useSetKeepReleaseRoomIntention();
    const [intention, setIntention] = useState<RebookingDecision | null>(null);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const rebookingInfoUrl = getRebookingInfoUrl(developmentSlug);

    useEffect(() => {
        if (!data?.rebookingDecision) return;

        const now = new Date();
        const start = new Date(data.rebookingDecision.start);
        const end = new Date(data.rebookingDecision.end);

        const isWithinDate = now >= start && now <= end;

        if (!data.rebookingDecision.intention && !open && isWithinDate) {
            openModal();
        }

        setIntention(data.rebookingDecision.intention);
    }, [data]);

    if (isLoading || !data?.rebookingDecision) return null;

    return (
        <Modal open={open} sx={{ height: '100%' }}>
            <Container
                maxWidth="md"
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <Paper
                    css={{ borderRadius: 0 }}
                    sx={{
                        my: '4vh',
                        overflowY: 'auto',
                        maxHeight: '80vh',
                    }}
                >
                    <RebookingModalHeader onClose={() => closeModal()} />
                    <Stack
                        alignItems="stretch"
                        width="100%"
                        padding={{ xs: '32px 16px', md: '36px' }}
                        gap="32px"
                    >
                        <Box>
                            <Typography variant="h2" mb="16px">
                                {t('rebooking_intention_modal_title')}
                            </Typography>
                            <Typography variant="body1">
                                {t('rebooking_intention_modal_subtitle')}
                            </Typography>
                        </Box>
                        <Grid container>
                            <RadioGroup sx={{ width: '100%' }}>
                                <Grid
                                    container
                                    columnSpacing="16px"
                                    rowSpacing="16px"
                                    alignItems="stretch"
                                >
                                    <Grid item xs={12} md={6}>
                                        <RebookingChoicesRadio
                                            label={t('rebooking_intention_modal_option_keep_label')}
                                            description={t(
                                                'rebooking_intention_modal_option_keep_description'
                                            )}
                                            checked={intention === RebookingDecision.HOLD}
                                            onChange={() => setIntention(RebookingDecision.HOLD)}
                                            value="keep"
                                            icon={IconLock}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <RebookingChoicesRadio
                                            label={t(
                                                'rebooking_intention_modal_option_release_label'
                                            )}
                                            description={t(
                                                'rebooking_intention_modal_option_release_description'
                                            )}
                                            checked={intention === RebookingDecision.RELEASE}
                                            onChange={() => setIntention(RebookingDecision.RELEASE)}
                                            value="release"
                                            icon={IconExit}
                                        />
                                    </Grid>
                                </Grid>
                            </RadioGroup>
                        </Grid>
                        <Divider />
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                            rowGap={2}
                        >
                            <Grid
                                item
                                xs={12}
                                md="auto"
                                spacing="8px"
                                {...(isSmallScreen && {
                                    display: 'flex',
                                    justifyContent: 'center',
                                })}
                            >
                                <b>
                                    <Link href={rebookingInfoUrl} target="_blank">
                                        {t('rebooking_intention_modal_find_out_more')}
                                    </Link>
                                </b>
                            </Grid>
                            <Grid item xs={12} md="auto">
                                <Button
                                    fullWidth={isSmallScreen}
                                    design="primary"
                                    loading={isSettingRebookIntention}
                                    disabled={intention === data.rebookingDecision.intention}
                                    onClick={() => {
                                        if (!intention) return;

                                        setRebookingIntention(
                                            { intention },
                                            {
                                                onSuccess() {
                                                    refetch();
                                                    closeModal();
                                                },
                                            }
                                        );
                                    }}
                                >
                                    {t('rebooking_intention_modal_confirm_choice')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Stack>
                </Paper>
            </Container>
        </Modal>
    );
};
